/* eslint-disable no-unsafe-optional-chaining */
import React, { useState, useEffect, useMemo, Fragment } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import axios from 'axios'
import { bool, string, object, array, shape, arrayOf, number, oneOf } from 'prop-types'

import useGenerationStatusWatcher from 'Pods/Shared/GenerationStatus/useGenerationStatusWatcher'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import useViewerId from 'Pods/Shared/Hooks/useViewerId'
import UserSegmentationModal from 'Pods/Shared/UserSegmentationModal'
import UserSubscriptionsModal from 'Pods/Shared/UserSubscriptionsModal'
import CgdreamNoticeBar from 'Pods/TopNotificationBars/Components/CgdreamNoticeBar'
import SaleOffBar from 'Pods/TopNotificationBars/Components/SaleOffBar'
import SubscribeBar, { DEFAULT_SUBSCRIPTION_PRICE } from 'Pods/TopNotificationBars/Components/SubscribeBar'

import { sendExperimentEvent } from 'Utils/Experiments'

import Navigation from './Components/Navigation/Navigation'

const navId = 'navigation-header'

function UnifiedNavigation({
  extractingCoreStyles = false,
  unifiedNavData = {
    barForbidden: false,
    commonRoutes: {},
    mainMenuItems: {},
    locale: 'en',
    byType: [],
  },
  isInFastPage = false,
  isInListingPage = false,
  searchInputValue = '',
  // props from slow pages
  isSignedIn = false,
  currentUserId: slowCurrentUserId = null,
  lastTimeModalSeenCookieKey,
  displaySegmentationModal,
  subscriptionsModalClosedCookieKey,
  displaySubscriptionsModal,
  barForbidden,
  newRoyaltyUi: slowRoyaltyUi,
  showCartAds: slowShowCartAds = false,
  // sale off bar props
  discountConfig = null,
  cgdreamNotice = false,
  subscriptionTopbar: slowSubscriptionTopbar = false,
  subscriptionPrice = DEFAULT_SUBSCRIPTION_PRICE,
  isActiveSubscriber: slowIsActiveSubscriber = false,
  subscriptionMenuEnabled: slowSubscriptionMenuEnabled = false,
  subscriptionFlowEnabled: slowSubscriptionFlowEnabled = false,
  showFreeTrialTag: slowShowFreeTrialTag = false,
  multipleGenerationsEnabled: slowMultipleGenerationsEnabled = false,
  subscriptionTier: slowSubscriptionTier = null,
  plansRestructureEnabled: slowPlansRestructureEnabled = false,
  subscriptionState: slowSubscriptionState = null,
}) {
  const {
    commonRoutes,
    mainMenuItems,
    communityLinks,
    createAiMenu,
    createAiGeneration,
    actionsLinks,
    locale,
    assetCategories,
    printableCategories,
    searchPlaceholder,
    searchDropdown,
    byType,
    budgetsData,
  } = unifiedNavData

  const pageConfig = usePageConfig({ listener: 'full-page-config-loaded', checkIfAvailable: false })
  const [subscriptionTopbar, setSubscriptionTopbar] = useState(slowSubscriptionTopbar)
  // CGDream sunset notice (/ai?cgdream) claims the top-bar slot; the promo bars are
  // already barForbidden on the studio, so it cannot actually race them.
  const [cgdreamNoticeDismissed, setCgdreamNoticeDismissed] = useState(false)
  const showCgdreamNotice = cgdreamNotice && !cgdreamNoticeDismissed
  const handleCgdreamDismiss = () => {
    setCgdreamNoticeDismissed(true)
    // The studio height offset is keyed on this server-rendered class
    // (three_d_studio.scss) because :has() is unavailable on older supported browsers.
    document.body.classList.remove('cgdream-notice-reserved')
  }
  const [actionsConfig, setActionsConfig] = useState({
    isCurrentUser: isSignedIn,
    cart: {},
    inboxSize: 0,
    notificationsSize: 0,
    newRoyaltyUi: slowRoyaltyUi,
    showCartAds: slowShowCartAds,
    subscriptionTopbar: slowSubscriptionTopbar,
    isActiveSubscriber: slowIsActiveSubscriber,
    subscriptionMenuEnabled: slowSubscriptionMenuEnabled,
    subscriptionFlowEnabled: slowSubscriptionFlowEnabled,
    subscriptionTier: slowSubscriptionTier,
    plansRestructureEnabled: slowPlansRestructureEnabled,
    subscriptionState: slowSubscriptionState,
  })
  const showSubscriptionBar = !!subscriptionTopbar && !barForbidden && !showCgdreamNotice
  const showSaleOff = !showSubscriptionBar && !!discountConfig && !barForbidden && !showCgdreamNotice
  // Free-trial tag in the Create with AI dropdown. On slow pages `current_user` is
  // available at SSR, so it arrives as a slow prop; on fast pages (no actor at SSR)
  // it arrives as the same per-user boolean in pageConfig via readPageConfig.
  const [showFreeTrialTag, setShowFreeTrialTag] = useState(slowShowFreeTrialTag)
  // Multiple-generations nav UI (count in the spinner, stacked result rows). Same split
  // as showFreeTrialTag: slow pages deliver the actor-aware Flipper check as an SSR prop
  // (current_user is available there); fast pages have no actor at SSR, so it starts off
  // and resolves per-user from pageConfig in readPageConfig.
  const [multipleGenerationsEnabled, setMultipleGenerationsEnabled] = useState(slowMultipleGenerationsEnabled)
  const createAiGenerationConfig = useMemo(
    () => ({ ...createAiGeneration, multipleGenerationsEnabled }),
    [createAiGeneration, multipleGenerationsEnabled]
  )

  const isInSlowPage = !isInFastPage

  // Viewer identity for the generation-status nav, normalized to a string. Slow pages
  // deliver the signed-in id deterministically as an SSR prop (current_user is available
  // there); fast pages have no actor at SSR, so it resolves client-side via useViewerId
  // once /load-services.js lands. Until it resolves the nav withholds any persisted run,
  // so a shared browser never leaks a generation across accounts.
  const clientViewerId = useViewerId()
  const currentUserId = slowCurrentUserId != null ? String(slowCurrentUserId) : clientViewerId

  // Site-wide producer that keeps the global generation-status store fed off /ai too.
  useGenerationStatusWatcher({ isCurrentUser: actionsConfig.isCurrentUser })

  function setToken() {
    const metaToken = document.getElementsByName('csrf-token')[0]
    let token = null

    if (metaToken) {
      token = metaToken.getAttribute('content')
      axios.defaults.headers.common['X-CSRF-Token'] = token
    }

    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
  }

  useEffect(() => {
    if (isInSlowPage) {
      setActionsConfig({
        isCurrentUser: isSignedIn,
        cart: {},
        inboxSize: 0,
        notificationsSize: 0,
        newRoyaltyUi: slowRoyaltyUi,
        showCartAds: slowShowCartAds,
        subscriptionTopbar: slowSubscriptionTopbar,
        isActiveSubscriber: slowIsActiveSubscriber,
        subscriptionMenuEnabled: slowSubscriptionMenuEnabled,
        subscriptionFlowEnabled: slowSubscriptionFlowEnabled,
        subscriptionTier: slowSubscriptionTier,
        plansRestructureEnabled: slowPlansRestructureEnabled,
        subscriptionState: slowSubscriptionState,
      })
    }
  }, [])

  useEffect(() => {
    if (!isInSlowPage && pageConfig) {
      readPageConfig()
    }
  }, [isInSlowPage, pageConfig])

  useEffect(() => {
    if (pageConfig) {
      sendExperimentEvent('disable_sitewide_discounts', pageConfig.disableSitewideDiscounts)
    }
  }, [pageConfig])

  useEffect(() => {
    const appHeader = document.getElementById('app-header')
    const notificationsContainer = document.getElementById('notifications-container')
    const hasTopBar = showSubscriptionBar || showSaleOff || showCgdreamNotice

    if (appHeader) {
      appHeader.classList.toggle('with-bar', extractingCoreStyles || hasTopBar)
    }

    if (notificationsContainer) {
      notificationsContainer.classList.toggle('with-bar', hasTopBar)
    }
  }, [extractingCoreStyles, showSubscriptionBar, showSaleOff, showCgdreamNotice])

  function readPageConfig() {
    setToken()

    const {
      cart,
      inboxSize,
      notificationsSize,
      isCurrentUser,
      newRoyaltyUi,
      showCartAds,
      subscriptionTopbar: configSubscriptionTopbar,
      isActiveSubscriber: configIsActiveSubscriber,
      subscriptionMenuEnabled: configSubscriptionMenuEnabled,
      subscriptionFlowEnabled: configSubscriptionFlowEnabled,
      showFreeTrialTag: configShowFreeTrialTag,
      threeDStudioMultipleGenerationsEnabled,
      subscriptionTier,
      plansRestructureEnabled,
      subscriptionAllowance,
    } = pageConfig

    // Only sent for signed-in viewers; signed-out visitors (key absent) see the tag.
    setShowFreeTrialTag(configShowFreeTrialTag !== false)

    // Absent for logged-out visitors (the pageConfig key is only set for signed-in
    // users), so the gate stays off in that case.
    if (typeof threeDStudioMultipleGenerationsEnabled === 'boolean') {
      setMultipleGenerationsEnabled(threeDStudioMultipleGenerationsEnabled)
    }

    window.dispatchEvent(
      new CustomEvent('navigation-cart-loaded', {
        detail: cart,
      })
    )

    setSubscriptionTopbar(configSubscriptionTopbar)

    setActionsConfig({
      cart,
      inboxSize,
      notificationsSize,
      isCurrentUser,
      newRoyaltyUi,
      showCartAds,
      subscriptionTopbar: configSubscriptionTopbar,
      subscriptionMenuEnabled: configSubscriptionMenuEnabled,
      subscriptionFlowEnabled: configSubscriptionFlowEnabled,
      isActiveSubscriber: configIsActiveSubscriber,
      subscriptionTier: subscriptionTier || null,
      plansRestructureEnabled: !!plansRestructureEnabled,
      subscriptionState: subscriptionAllowance?.state || null,
    })
  }

  return (
    <nav
      id={navId}
      className={classnames(
        'wayfinder',
        (extractingCoreStyles || showSubscriptionBar || showSaleOff || showCgdreamNotice) && 'with-bar'
      )}
    >
      {showCgdreamNotice && (
        <CgdreamNoticeBar locale={locale} onDismiss={handleCgdreamDismiss} />
      )}

      {showSubscriptionBar && (
        <SubscribeBar price={subscriptionPrice} locale={locale} />
      )}

      {showSaleOff && (
        <SaleOffBar
          barColor={discountConfig.barColor}
          content={discountConfig.body}
          discountPhrase={discountConfig.discountPhrase}
          discount={discountConfig.noticeBarDiscount}
          endingDate={discountConfig.endingDate}
          useTimer={discountConfig.useTimer}
          locale={locale}
        />
      )}

      <Navigation
        commonRoutes={commonRoutes}
        mainMenuItems={mainMenuItems}
        communityLinks={communityLinks}
        createAiMenu={createAiMenu}
        createAiGeneration={createAiGenerationConfig}
        showFreeTrialTag={showFreeTrialTag}
        actionsLinks={actionsLinks}
        actionsConfig={actionsConfig}
        locale={locale}
        isInListingPage={isInListingPage}
        searchInputValue={searchInputValue}
        searchPlaceholder={searchPlaceholder}
        searchDropdown={searchDropdown}
        currentUserId={currentUserId}
        assetCategories={assetCategories}
        printableCategories={printableCategories}
        byType={byType}
        budgetsData={budgetsData}
      />

      {isInSlowPage && (
        <Fragment>
          <UserSegmentationModal cookieKey={lastTimeModalSeenCookieKey} isModalOpen={displaySegmentationModal} />
          <UserSubscriptionsModal
            lastSeenCookieKey={lastTimeModalSeenCookieKey}
            modalClosedCookieKey={subscriptionsModalClosedCookieKey}
            isModalOpen={displaySubscriptionsModal}
          />
        </Fragment>
      )}
    </nav>
  )
}

UnifiedNavigation.propTypes = {
  extractingCoreStyles: bool,
  isInFastPage: bool,
  isInListingPage: bool,
  searchInputValue: string,
  unifiedNavData: shape({
    barForbidden: bool,
    commonRoutes: object,
    mainMenuItems: object,
    communityLinks: array,
    createAiMenu: object,
    createAiGeneration: object,
    actionsLinks: arrayOf(shape({
      id: string,
      href: string,
      icon: string,
      data: shape({
        trackText: string,
        trackLabel: string,
        avoidNavigation: bool,
      }),
    })).isRequired,
    locale: string,
    assetCategories: object,
    printableCategories: object,
    searchDropdown: object,
    budgetsData: shape({
      assetsBaseUrl: string.isRequired,
      printableBaseUrl: string.isRequired,
      assetsBudgets: arrayOf(shape({
        title: string.isRequired,
        path: string.isRequired,
        icon: string.isRequired,
        withSub: bool,
      })).isRequired,
      printableAssetsBudgets: arrayOf(shape({
        title: string.isRequired,
        path: string.isRequired,
        icon: string.isRequired,
        withSub: bool,
      })).isRequired,
    }).isRequired,
  }),
  isSignedIn: bool,
  currentUserId: number,
  showCartAds: bool,
  subscriptionTopbar: bool,
  subscriptionPrice: string,
  isActiveSubscriber: bool,
  subscriptionMenuEnabled: bool,
  subscriptionFlowEnabled: bool,
  showFreeTrialTag: bool,
  multipleGenerationsEnabled: bool,
  subscriptionTier: oneOf(['essentials', 'premium']),
  plansRestructureEnabled: bool,
  subscriptionState: oneOf(['active', 'paused', 'payment_failed', 'none']),
  cgdreamNotice: bool,
  discountConfig: shape({
    barColor: string,
    body: string,
    discountPhrase: string,
    noticeBarDiscount: number,
    endingDate: number,
    useTimer: bool,
  }),
}

export default UnifiedNavigation
